import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LEVERS } from "../lib/site";

const TONE = {
    vantera: { activeBg: "bg-vantera-soft border-vantera/40", chip: "text-vantera border-vantera/40", dot: "bg-vantera" },
    azure: { activeBg: "bg-azure-soft border-azure/40", chip: "text-azure border-azure/40", dot: "bg-azure" },
    ink: { activeBg: "bg-ink text-ivory border-ink", chip: "text-vantera border-vantera/50", dot: "bg-vantera" },
};

const ProfitLevers = () => {
    const [active, setActive] = useState(0);

    return (
        <div data-testid="profit-levers">
            <div className="flex flex-col gap-3 lg:h-[540px] lg:flex-row">
                {LEVERS.map((lv, i) => {
                    const isActive = active === i;
                    const tone = TONE[lv.tone];
                    return (
                        <motion.div
                            key={lv.id}
                            data-testid={`profit-lever-${lv.id}`}
                            onMouseEnter={() => setActive(i)}
                            onClick={() => setActive(i)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setActive(i)}
                            aria-expanded={isActive}
                            className={`relative cursor-pointer overflow-hidden rounded-2xl border p-7 sm:p-9 transition-colors duration-500 lg:transition-[flex-grow] lg:duration-700 lg:ease-[cubic-bezier(0.16,1,0.3,1)] ${isActive ? tone.activeBg : "bg-white border-ink/10"}`}
                            style={{ flexGrow: isActive ? 2.4 : 1, flexBasis: 0 }}
                        >
                            <p className={`font-mono text-xs tracking-[0.24em] ${isActive ? (lv.tone === "ink" ? "text-cloud" : "text-slategray") : "text-slategray"}`}>{lv.num}</p>
                            <div className="mt-6 flex items-baseline justify-between gap-4 lg:flex-col lg:items-start">
                                <h3 className="font-display text-4xl font-extrabold tracking-tightest sm:text-5xl">{lv.name}</h3>
                                <p className={`font-mono text-[11px] uppercase tracking-[0.2em] lg:mt-2 ${isActive ? (lv.tone === "ink" ? "text-vantera" : "text-slategray") : "text-slategray"}`}>{lv.sub}</p>
                            </div>

                            <AnimatePresence initial={false}>
                                {isActive && (
                                    <motion.div
                                        key="body"
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                        className="overflow-hidden"
                                    >
                                        <p className={`mt-6 max-w-md text-[17px] leading-relaxed ${lv.tone === "ink" ? "text-ivory/80" : "text-slategray"}`}>{lv.blurb}</p>
                                        <ul className="mt-6 grid gap-x-8 gap-y-2.5 sm:grid-cols-2 lg:max-w-lg">
                                            {lv.caps.map((c, j) => (
                                                <motion.li key={c} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + j * 0.05 }} className={`flex items-center gap-2.5 text-[15px] font-medium ${lv.tone === "ink" ? "text-ivory" : "text-ink"}`}>
                                                    <span className={`inline-block size-1.5 rotate-45 ${tone.dot}`} aria-hidden="true" />
                                                    {c}
                                                </motion.li>
                                            ))}
                                        </ul>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <span className={`absolute bottom-0 left-0 h-[3px] w-full origin-left transition-transform duration-700 ${isActive ? "scale-x-100" : "scale-x-0"} ${tone.dot}`} aria-hidden="true" />
                        </motion.div>
                    );
                })}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl border border-ink/10 bg-white px-6 py-5" data-testid="profit-formula">
                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-slategray">The outcome</span>
                {LEVERS.map((lv, i) => (
                    <span key={lv.id} className="flex items-center gap-4">
                        <span className={`font-display text-lg font-extrabold tracking-tight ${lv.tone === "vantera" ? "text-vantera" : lv.tone === "azure" ? "text-azure" : "text-ink"}`}>{lv.name}</span>
                        {i < 2 && <span className="font-mono text-slategray">+</span>}
                    </span>
                ))}
                <span className="font-mono text-xl text-vantera" aria-hidden="true">→</span>
                <span className="font-display text-lg font-extrabold tracking-tight text-ink">MORE PROFIT</span>
            </div>
        </div>
    );
};

export default ProfitLevers;